import { z } from 'zod';

/** File kinds (DESIGN §7.2). `other` is the catch-all for files no layout rule names. */
export const fileKind = z.enum([
  'source-drawing',
  'source-index',
  'raster',
  'overlay',
  'photo',
  'point-cloud',
  'reading',
  'ledger',
  'report',
  'critic',
  'model-source',
  'model-build',
  'ifc',
  'deliverable',
  'page',
  /** The workspace's own files (CLAUDE.md, LOG.md, dotfiles): part of the record, internal by default. */
  'workspace',
  'other',
]);
export type FileKind = z.infer<typeof fileKind>;

const str = z.string();
const int = z.number().int();
const num = z.number();
const bool = z.boolean();
const vec = z.array(z.number());
const checks = z.object({
  passed: bool,
  count: int.nonnegative().optional(),
  failed: int.nonnegative().optional(),
  log: str.optional(),
});
const fromRef = z.object({
  path: str,
  version: int.positive().optional(),
  sha256: str.optional(),
});
const reviewShape = {
  storey: str.optional(),
  pass: z.union([int, str]).optional(),
  verdict: str.optional(),
  author: str.optional(),
};
const rasterShape = {
  from: fromRef.optional(),
  dpi: num.optional(),
  calibration: z
    .object({
      residual_cm: num.optional(),
      points: z.union([int, z.array(z.unknown())]).optional(),
    })
    .loose()
    .optional(),
  plane: str.optional(),
  at: vec.optional(),
  u: vec.optional(),
  v: vec.optional(),
};
const modelShape = {
  buildHash: str.optional(),
  checks: checks.optional(),
};

/** Known metadata keys per kind. Every key is optional; anything else is kept under `extra`. */
export const metadataShapes = {
  'source-drawing': {
    archive: str.optional(),
    case: str.optional(),
    landNumber: str.optional(),
    address: str.optional(),
    date: str.optional(),
    status: str.optional(),
    type: str.optional(),
    description: str.optional(),
    designer: str.optional(),
    sourceUrl: str.optional(),
    pages: int.nonnegative().optional(),
    scanned: bool.optional(),
    title: str.optional(),
    sheetNumber: str.optional(),
    scale: str.optional(),
    storey: str.optional(),
  },
  'source-index': {
    archive: str.optional(),
    fetchedAt: str.optional(),
    count: int.nonnegative().optional(),
  },
  raster: rasterShape,
  overlay: rasterShape,
  photo: {
    takenAt: str.optional(),
    source: str.optional(),
    viewpoint: str.optional(),
    note: str.optional(),
  },
  'point-cloud': {
    surveyor: str.optional(),
    date: str.optional(),
    crs: str.optional(),
    epsg: int.positive().optional(),
    heightDatum: str.optional(),
    pointCount: int.nonnegative().optional(),
    accuracy: z.union([num, str]).optional(),
    format: str.optional(),
  },
  reading: reviewShape,
  ledger: reviewShape,
  report: reviewShape,
  critic: reviewShape,
  'model-source': modelShape,
  'model-build': modelShape,
  ifc: {
    schema: str.optional(),
    role: str.optional(),
    products: int.nonnegative().optional(),
    validation: z.union([str, z.record(z.string(), z.unknown())]).optional(),
    roundTrip: z.union([bool, str, z.record(z.string(), z.unknown())]).optional(),
    author: str.optional(),
  },
  deliverable: {
    audience: str.optional(),
    description: str.optional(),
  },
  /** `page.json` of an AI-written page (docs/PAGES.md); the metadata is the parsed file. */
  page: {
    p2b: str.optional(),
    id: str.optional(),
    title: str.optional(),
    entry: str.optional(),
    audience: str.optional(),
    description: str.optional(),
    madeFrom: z.record(z.string(), z.unknown()).optional(),
    author: str.optional(),
    files: z.array(str).optional(),
  },
  workspace: {},
  other: {},
} satisfies Record<FileKind, z.ZodRawShape>;

const extra = z.record(z.string(), z.unknown());

/** The stored (canonical) form of a kind's metadata: known keys typed, the rest under `extra`. */
export function metadataSchema(kind: FileKind) {
  return z.strictObject({ ...metadataShapes[kind], extra: extra.optional() });
}

/** The lenient form the kit validates against before upload: known keys typed, unknown keys allowed. */
export function metadataInputSchema(kind: FileKind) {
  return z.looseObject({ ...metadataShapes[kind], extra: extra.optional() });
}

export type Metadata = Record<string, unknown> & { extra?: Record<string, unknown> };

function unwrap(schema: z.ZodType): z.ZodType {
  let s: any = schema;
  while (s instanceof z.ZodOptional || s instanceof z.ZodNullable || s instanceof z.ZodDefault)
    s = s.unwrap();
  return s;
}

const TRUE = new Set(['true', '1', 'yes', 'y', 'já', 'ja']);
const FALSE = new Set(['false', '0', 'no', 'n', 'nei']);

/** Coerce a value read from CSV or a `key=value` argument towards the field's type. Leaves it alone otherwise. */
function coerce(schema: z.ZodType, value: unknown): unknown {
  const base = unwrap(schema);
  if (typeof value === 'string') {
    const s = value.trim();
    if (base instanceof z.ZodNumber) {
      if (s !== '' && Number.isFinite(Number(s))) return Number(s);
      return value;
    }
    if (base instanceof z.ZodBoolean) {
      const l = s.toLowerCase();
      if (TRUE.has(l)) return true;
      if (FALSE.has(l)) return false;
      return value;
    }
    if (base instanceof z.ZodUnion) {
      const opts = (base as any).options as z.ZodType[];
      if (opts.some((o) => unwrap(o) instanceof z.ZodString)) return value;
      for (const o of opts) {
        const c = coerce(o, value);
        if (c !== value) return c;
      }
      return value;
    }
    if (
      (base instanceof z.ZodObject || base instanceof z.ZodArray) &&
      (s.startsWith('{') || s.startsWith('['))
    ) {
      try {
        return JSON.parse(s);
      } catch {
        return value;
      }
    }
    return value;
  }
  if (typeof value === 'number' && base instanceof z.ZodString) return String(value);
  return value;
}

/**
 * Normalize metadata for a kind: drop empty values, coerce strings (CSV) to the field's type, move unknown keys
 * under `extra`, then validate. Throws a ZodError when a known key has the wrong type.
 */
export function normalizeMetadata(kind: FileKind, raw: unknown): Metadata {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return metadataSchema(kind).parse(raw) as Metadata;
  }
  const shape = metadataShapes[kind] as Record<string, z.ZodType>;
  const known: Record<string, unknown> = {};
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'extra') {
      if (typeof value === 'object' && !Array.isArray(value)) Object.assign(extras, value);
      else extras.extra = value;
      continue;
    }
    if (Object.hasOwn(shape, key)) known[key] = coerce(shape[key]!, value);
    else extras[key] = value;
  }
  if (Object.keys(extras).length > 0) known.extra = extras;
  return metadataSchema(kind).parse(known) as Metadata;
}

/** Merge a metadata patch into existing metadata (both normalized); `null` in the patch removes a key. */
export function mergeMetadata(kind: FileKind, current: Metadata, patch: Record<string, unknown>): Metadata {
  const flat: Record<string, unknown> = { ...current, ...(current.extra ?? {}) };
  delete flat.extra;
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'extra' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [ek, ev] of Object.entries(v)) {
        if (ev === null) delete flat[ek];
        else flat[ek] = ev;
      }
    } else if (v === null) delete flat[k];
    else flat[k] = v;
  }
  return normalizeMetadata(kind, flat);
}

const WORKSPACE_ROOT_FILES = new Set([
  'CLAUDE.md',
  'AGENTS.md',
  'LOG.md',
  'SCOPE_PROPOSAL.md',
  'p2b.json',
  'platform_notes.md',
]);

/**
 * A file that belongs to the workspace rather than to the building: the agreement, the log, the p2b config and
 * dotfiles or dot-folders at any depth (`.gitignore`, `.claude/launch.json`). The CLI pushes these as kind
 * `workspace` (internal) when no layout rule names them; the web app also hides older ones pushed as `other`.
 */
export function isWorkspacePath(path: string): boolean {
  const parts = path.split('/');
  if (parts.some((p) => p.startsWith('.'))) return true;
  return parts.length === 1 && WORKSPACE_ROOT_FILES.has(parts[0]!);
}
